import MuleBadge from '../../components/MuleBadge';
import React, { useEffect, useState, useMemo } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import PermissionsContainer from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import { config } from '../../config/config';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

interface VinMismatchData {
  unitnumber: string;
  isMule?: boolean;
  drivername: string | null;
  pronumber: string | null;
  v1_vin: string | null;
  samsara_vin: string | null;
  dispatcher: string | null;
  issue_type: string;
}

const VinMismatch: React.FC = () => {
  const [trucks, setTrucks] = useState<VinMismatchData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Acknowledge (engine-swap override) modal state
  const [ackTarget, setAckTarget] = useState<VinMismatchData | null>(null);
  const [ackReason, setAckReason] = useState('');
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const {
    panelRef: ackPanelRef,
    panelStyle: ackPanelStyle,
    dragHandleProps: ackDragHandleProps,
  } = useModalDrag(ackTarget !== null);

  const { setPageComponents } = usePermissionsMode();

  // Register page components for permissions mode
  const pageComponentsList = useMemo(() => [
    { id: 'vin-mismatch-header', name: 'Page Header' },
    { id: 'vin-mismatch-table', name: 'VIN Mismatch Table' },
    { id: 'vin-mismatch-acknowledge', name: 'Acknowledge Mismatch Action' },
  ], []);

  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/fleet-management/vin-mismatch');
      setTrucks(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch driver data issues');
      setTrucks([]);
    } finally {
      setLoading(false);
    }
  };

  const submitAcknowledge = async () => {
    if (!ackTarget) return;
    setAckSubmitting(true);
    setAckError(null);
    try {
      const res = await api.post('/fleet-management/vin-override', {
        unitnumber: ackTarget.unitnumber,
        v1_vin: ackTarget.v1_vin,
        samsara_vin: ackTarget.samsara_vin,
        reason: ackReason.trim() || null,
      });
      if (res.data?.success === false) {
        throw new Error(res.data?.error || 'Failed to acknowledge');
      }
      // Optimistically drop the acknowledged row, then refetch to stay in sync.
      setTrucks((prev) => prev.filter((t) => t.unitnumber !== ackTarget.unitnumber));
      setAckTarget(null);
      setAckReason('');
      fetchData();
    } catch (err: any) {
      setAckError(err.response?.data?.detail || err.message || 'Failed to acknowledge');
    } finally {
      setAckSubmitting(false);
    }
  };

  const closeAck = () => {
    if (ackSubmitting) return;
    setAckTarget(null);
    setAckReason('');
    setAckError(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const getIssueTypeStyle = (issueType: string) => {
    if (issueType.startsWith('DL Mismatch')) {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
    switch (issueType) {
      case 'No V1 VIN':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'V1 Truck Deleted':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400';
      case 'Not in Samsara':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'VIN Mismatch':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'Not in Location Cache':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <PermissionsContainer componentId="vin-mismatch-header" componentName="Page Header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-heading">
              Driver Data Issues
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Drivers with VIN or license mismatches between V1 and Samsara
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-md disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </PermissionsContainer>

      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <PermissionsContainer componentId="vin-mismatch-table" componentName="VIN Mismatch Table">
        <div className="bg-panel shadow rounded-lg overflow-hidden">
          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-750 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-label">
                {loading ? 'Loading...' : `${trucks.length} driver${trucks.length !== 1 ? 's' : ''} with data issues`}
              </span>
              <span className="text-xs text-muted">
                Auto-refreshes every 60 seconds
              </span>
            </div>
          </div>

          {/* Loading State */}
          {loading && trucks.length === 0 ? (
            <div className="p-8 text-center">
              <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin mx-auto" />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading driver data...</p>
            </div>
          ) : trucks.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
              <p className="mt-2 text-lg font-medium text-heading">All Clear</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                All drivers have matching data between V1 and Samsara
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-100 dark:bg-gray-750">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Unit
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Driver
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      PRO
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      V1 VIN
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Samsara VIN
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Issue
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Dispatcher
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-panel divide-y divide-gray-200 dark:divide-gray-700">
                  {trucks.map((truck, index) => (
                    <tr key={`${truck.unitnumber}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <a
                          href={`${config.V2_URL}/safety/drivers/${parseInt(truck.unitnumber, 10)}/driver`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {truck.unitnumber}<MuleBadge isMule={truck.isMule} />
                        </a>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-label">
                        {truck.drivername || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">
                        {truck.pronumber || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-label">
                        {truck.v1_vin ? (
                          <span>{truck.v1_vin}</span>
                        ) : (
                          <span className="text-red-500">Missing</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-label">
                        {truck.samsara_vin ? (
                          <span>{truck.samsara_vin}</span>
                        ) : (
                          <span className="text-red-500">Missing</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getIssueTypeStyle(truck.issue_type)}`}>
                          {truck.issue_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-label">
                        {truck.dispatcher || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        {truck.issue_type === 'VIN Mismatch' && (
                          <PermissionsContainer componentId="vin-mismatch-acknowledge" componentName="Acknowledge Mismatch Action">
                            <button
                              onClick={() => {
                                setAckError(null);
                                setAckReason('');
                                setAckTarget(truck);
                              }}
                              title="Acknowledge this as a known engine-swap mismatch (stops flagging)"
                              className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 dark:text-amber-400 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 rounded-md"
                            >
                              Acknowledge
                            </button>
                          </PermissionsContainer>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </PermissionsContainer>

      {/* Acknowledge (engine-swap override) modal */}
      {ackTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4"
          onClick={closeAck}
        >
          <div
            ref={ackPanelRef}
            style={ackPanelStyle}
            className="w-full max-w-md rounded-lg bg-panel shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={`Acknowledge VIN Mismatch — Unit ${ackTarget.unitnumber}`}
              onClose={ackSubmitting ? undefined : closeAck}
              dragHandleProps={ackDragHandleProps}
            />
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-muted">
                Records this as a known, accepted VIN pairing (e.g. engine swap). It stops
                flagging while both sides keep reporting these VINs; a future swap re-flags it.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted">V1 VIN</div>
                  <div className="text-gray-800 dark:text-gray-200 break-all">{ackTarget.v1_vin || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted">Samsara VIN</div>
                  <div className="text-gray-800 dark:text-gray-200 break-all">{ackTarget.samsara_vin || '-'}</div>
                </div>
              </div>
              <div>
                <label htmlFor="ack-reason" className="block text-xs font-medium text-label mb-1">
                  Reason (optional)
                </label>
                <textarea
                  id="ack-reason"
                  value={ackReason}
                  onChange={(e) => setAckReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. engine swap 6/2026 — chassis VIN unchanged"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-body px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {ackError && (
                <p className="text-sm text-red-600 dark:text-red-400">{ackError}</p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={closeAck}
                disabled={ackSubmitting}
                className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitAcknowledge}
                disabled={ackSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
              >
                {ackSubmitting ? 'Saving…' : 'Acknowledge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VinMismatch;
